import { BookingEngine, Rates, roomData } from "../../index";
import Selectors from "./Selectors";
import waitForSelectors from "../../utilities/waitForSelectors";
import { handleIntegrationInjector } from "../../hotelIntegrationHandlers";
import {
  setNativeValue,
  getBrowserInfo,
  getDeviceInfo,
  parsePrice,
} from "../../utilities/utils";

declare global {
  interface Window {
    gb_data: any;
    gb_dotDecimalSeparated: any;
    totalPriceForStay: any;
    CBData: any;
    CBBooking: any;
  }
}

const gbData = window.gb_data;

window.gb_dotDecimalSeparated = [
  "en",
  "et",
  "pl",
  "nl",
  "ja",
  "th",
  "cn",
  "he",
  "ko",
].includes(window.CBBooking.current_lang);

const Cloudbeds: BookingEngine = {
  name: gbData.bookingEngine,
  hotel: {
    name: gbData.hotel.name,
    isAutomated: gbData.hotel.isAutomated,
    rateCodeExclusions: gbData.hotel.rateCodeExclusions,
    packageRateExlusions: gbData.hotel.packageRateExlusions,
    whiteLabel: gbData.hotel.whiteLabel,
    dontAbTrack: true,
    memberCode: gbData.hotel.memberCode,
    memberCodeType: gbData.hotel.memberCodeType,
    showEnrollment: JSON.parse(
      sessionStorage.getItem("gbShowEnrollment") || "true"
    ),
    widgetIdentifier: gbData.widget.identifier,
    primaryColor: gbData.widget.primaryColor,
    secondaryColor: gbData.widget.secondaryColor,
    widgetAnchored: gbData.widget.widgetAnchored,
    widgetWidth: gbData.widget.widgetWidth,
    widgetPositionTop: gbData.widget.widgetPositionTop,
    widgetPositionRight: gbData.widget.widgetPositionRight,
    widgetPositionBottom: gbData.widget.widgetPositionBottom,
    widgetPositionLeft: gbData.widget.widgetPositionLeft,
    inheritBeFont: gbData.widget.inheritBeFont,
    desktopEnrollmentPrimaryColor: gbData.widget.desktopEnrollmentPrimaryColor,
    benefits: gbData.widget.benefits,
  },
  selectors: Selectors,
  singlePageApp: true,
  widgetFixedPosition: !gbData.widget.widgetAnchored,
  widgetMobileAtWidth: 991,
  largeEnrollmentMessage: true,
  activeIntervals: [],
  // grab widget target
  getWidgetTarget: () =>
    document.querySelector(Cloudbeds.selectors.widget.target),
  // not yet implemented in be
  getMemberCode: () => {},
  // not yet implemented in be
  clearMemberCode: () => {},
  // grab rates info and target to inject
  getRates: () => {
    const ratesInfo: Rates[] = [];
    const rates= document.querySelectorAll(
      Cloudbeds.selectors.ratesPage.rates
    );

    rates.forEach((rate: any) => {
      const messageTarget = rate.classList.contains("new-price") ? (rate as HTMLElement).parentElement.parentElement : (rate as HTMLElement).parentElement;

      if (messageTarget) {
        const injectedElement = !!messageTarget.querySelector(
          Cloudbeds.selectors.ratesPage.ratesMessage
        )
          ? messageTarget.querySelector(
              Cloudbeds.selectors.ratesPage.ratesMessage
            )
          : document.createElement("div");
        if (
          !messageTarget.querySelector(
            Cloudbeds.selectors.ratesPage.ratesMessage
          ) &&
          injectedElement
        ) {
          injectedElement.setAttribute(
            "class",
            Cloudbeds.selectors.ratesPage.ratesMessage.substr(1)
          );
          messageTarget.insertAdjacentElement("afterend", injectedElement);
        }

        const taxedRate = window.gb_data.hotel.taxedRate;
        let ratePriceText = (rate as HTMLElement).innerText;
        let ratePrice = parsePrice(ratePriceText);

        let calculatedRewards = (ratePrice / taxedRate) * 0.15;
        let shouldDisplay = true;

        ratesInfo.push({
          rewardsAmount: (calculatedRewards as any).toFixed(0),
          shouldDisplay: shouldDisplay,
          target: injectedElement,
        });
      }
    });
    sessionStorage.setItem("rates", JSON.stringify(ratesInfo));
    return ratesInfo;
  },
  // grab confirmation number
  getConfirmationNumber: () => [
    document.querySelector(
      Cloudbeds.selectors.confirmationPage.confirmationNumber
    ).innerText,
  ],
  // grab confirmation message target
  getConfirmationTarget: () =>
    document.querySelector(
      Cloudbeds.selectors.confirmationPage.confirmationMessageContainer
    ),
  // grab displayed rates page currency
  getRatesCurrency: () => {
    if (window.CBBooking.formats.currency_symbol)
      return window.CBBooking.formats.currency_symbol

    const currency = document
      .querySelector(Cloudbeds.selectors.widget.currency)
      .innerText.replace(/\d+.\d+/g, "")
      .trim();
    if (currency.match(/[a-z]/i)) return `${currency} `;
    return currency;
  },
  // grab displayed checkout page currency
  getCheckoutCurrency: () => {
    if (window.CBBooking.formats.currency_symbol)
      return window.CBBooking.formats.currency_symbol
      
    const currency = document
      .querySelector(Cloudbeds.selectors.widget.currency)
      .innerText.replace(/\d+.\d+/g, "")
      .trim();
    if (currency.match(/[a-z]/i)) return `${currency} `;
    return currency;
  },
  // grab currency code to save
  getCurrencyToSave: () => window.CBBooking.user_currency.currency_code,
  // grab language
  getLanguage: () => window.CBBooking.current_lang,
  // grab enrollment target
  getEnrollmentTarget: () =>
    document.querySelector(
      Cloudbeds.selectors.paymentPage.enrollmentMessageContainer
    ),
  // grab checkin date
  getPaymentPageCheckinDate: () => window.CBData.checkin,
  // grab checkout date
  getPaymentPageCheckoutDate: () => window.CBData.checkout,
  // prefill user info if user logged in
  prefillUserInfo: (firstName = "", lastName = "", email = "") => {
    waitForSelectors(Cloudbeds.selectors.paymentPage.waitFor, () => {
      const emailInput = document.querySelector(
        Cloudbeds.selectors.paymentPage.email
      );
      const firstNameInput = document.querySelector(
        Cloudbeds.selectors.paymentPage.firstName
      );
      const lastNameInput = document.querySelector(
        Cloudbeds.selectors.paymentPage.lastName
      );
      const event = new Event("input", { bubbles: true });

      setNativeValue(firstNameInput, firstName);
      firstNameInput.focus();
      firstNameInput.dispatchEvent(event);
      firstNameInput.blur();

      setNativeValue(lastNameInput, lastName);
      lastNameInput.focus();
      lastNameInput.dispatchEvent(event);
      lastNameInput.blur();

      setNativeValue(emailInput, email);
      emailInput.focus();
      emailInput.dispatchEvent(event);
      emailInput.blur();
    });
  },
  // grab email
  getGuestEmail: () =>
    document.querySelector(Cloudbeds.selectors.paymentPage.email).value.trim(),
  // grab first name
  getGuestFirstName: () =>
    document
      .querySelector(Cloudbeds.selectors.paymentPage.firstName)
      .value.trim(),
  // grab last name
  getGuestLastName: () =>
    document
      .querySelector(Cloudbeds.selectors.paymentPage.lastName)
      .value.trim(),
  // grab rate type
  getRateType: () => {
    let rateNames = "";
    const rateTypesObject = window.CBBooking.accomodation_types_names;
    const rateTypesKeys = Object.keys(rateTypesObject);
    const roomNamesContainers = document.querySelectorAll(
      Cloudbeds.selectors.paymentPage.roomNames
    );
    roomNamesContainers.forEach((e: any, i: number) => {
      const totalOfRooms = Number(
        e.innerText
          .match(/\((.*)\)/)
          .pop()
          ?.replace(/[^0-9]/g, "")
      );
      for (let j = 0; j < totalOfRooms; j++) {
        // add name
        rateNames += e.innerText?.split("(").shift().trim() + ' ';
        if (i === roomNamesContainers.length - 1 && j === totalOfRooms - 1) {
          // add number
          rateNames += rateTypesKeys.find(
            (key: any) =>
              rateTypesObject[key] === e.innerText?.split("(").shift().trim()
          );
        } else {
          rateNames += `${rateTypesKeys.find(
            // add number
            (key: any) =>
              rateTypesObject[key] === e.innerText?.split("(").shift().trim()
          )}, `;
        }
      }
    });
    return rateNames;
  },
  // grab roomName
  getRoomName: () => {
    let roomNames = "";
    const roomNamesContainers = document.querySelectorAll(
      Cloudbeds.selectors.paymentPage.roomNames
    );
    roomNamesContainers.forEach((e: any, i: number) => {
      if (i === roomNamesContainers.length - 1 ) {
        roomNames += e.innerText?.split("\n").shift().trim();
      } else {
        roomNames += `${e.innerText?.split("\n").shift().trim()}, `;
      }
    });
    return roomNames;
  },
  getNumberOfAdults: () => window.CBData.adults,
  getNumberOfDays: () => window.CBData.nights,
  // grab rateCodes
  getRateTypes: () => [],
  // grab promo code applied
  getPromoCode: () => {
    let promoCode = "";
    if (window.CBBooking.promo_code) {
      promoCode = window.CBBooking.promo_code;
    }
    return promoCode;
  },
  // grab group code applied
  // Doesn't seem to have field in window, check back when we have one to check with
  getGroupCode: () => "",
  // grab subtotal to save
  getSubtotal: () => {
    let subtotal = document
      .querySelector(Cloudbeds.selectors.paymentPage.subtotal)
      .innerText.replace(Cloudbeds.getRatesCurrency(), "")
      .trim();
    if (!window.gb_dotDecimalSeparated) {
      subtotal = subtotal.replace(".", "").replace(",", ".");
    }
    subtotal = parsePrice(subtotal);
    return subtotal;
  },
  // grab grand total to save
  getTotalWithTaxes: () => {
    let totalWithTaxes = document
      .querySelector(Cloudbeds.selectors.paymentPage.totalWithTaxes)
      .innerText.replace(Cloudbeds.getRatesCurrency(), "")
      .trim();
    if (!window.gb_dotDecimalSeparated) {
      totalWithTaxes = totalWithTaxes.replace(".", "").replace(",", ".");
    }
    totalWithTaxes = parsePrice(totalWithTaxes);
    return totalWithTaxes;
  },
  // aggregrate reservation data
  getReservationData: () => {
    let resData: roomData[] = [];

    if (Cloudbeds.isRateExcluded && Cloudbeds.getRateExcluded) {
      let shouldSave = true;
      if (
        Cloudbeds.isRateExcluded(Cloudbeds.getRateType() as string) &&
        Cloudbeds.getRateExcluded(Cloudbeds.getRateType() as string).rate ===
          "0.0"
      ) {
        shouldSave = false;
      }

      if (shouldSave) {
        resData.push({
          email: Cloudbeds.getGuestEmail(),
          first_name: Cloudbeds.getGuestFirstName(),
          last_name: Cloudbeds.getGuestLastName(),
          confirmation_number: "",
          checkin_date: Cloudbeds.getPaymentPageCheckinDate(),
          checkout_date: Cloudbeds.getPaymentPageCheckoutDate(),
          rate_type: "",
          room_name: Cloudbeds.getRoomName() as string,
          currency_code: Cloudbeds.getCurrencyToSave(),
          lang_code: Cloudbeds.getLanguage(),
          total: (Cloudbeds.getSubtotal() as number).toString(),
          total_with_taxes: (Cloudbeds.getTotalWithTaxes() as number).toString(),
          hotel_identifier: window.gb_data.widget.identifier,
          agreed_enrollment_checked: sessionStorage.getItem(
            "gbEnrolledChecked"
          ) as string,
          ab_version_viewed: sessionStorage.getItem(
            "gbShowEnrollment"
          ) as string,
          browser_info: getBrowserInfo(),
          is_mobile: getDeviceInfo(),
          promo_code:
            Cloudbeds.getPromoCode && Cloudbeds.getPromoCode()
              ? Cloudbeds.getPromoCode()
              : "",

          group_code: Cloudbeds.getGroupCode ? Cloudbeds.getGroupCode() : "",
        });
      }
    }

    return resData;
  },
  // grabs cash rewards
  getRewards: (percentage: number) => {
    let calculatedRewards = 0;
    let subtotal = Cloudbeds.displaySubtotal() / window.gb_data.hotel.taxedRate;

    calculatedRewards = Number((subtotal * percentage).toFixed(2));

    return calculatedRewards.toFixed(2);
  },
  // not yet implemented in be
  isRateExcluded: () => false,
  // not yet implemented in be
  getRateExcluded: () => {},
  // checks if messaging should be displayed
  shouldDisplayMessaging: () => {
    // if show enrollment (from AB is set to false)
    if (!JSON.parse(sessionStorage.getItem("gbShowEnrollment") || "true"))
      return false;
    // check applied promo & block codes
    const promoCode = Cloudbeds.getPromoCode ? Cloudbeds.getPromoCode() : "";
    const blockCode = window.CBBooking.allotment_block_code ? window.CBBooking.allotment_block_code : "";
    const rateCodeExclusions = Cloudbeds.hotel.rateCodeExclusions;
    for (const rateCodeExclusion of rateCodeExclusions as any) {
      // Do not show
      if (
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          promoCode.toLowerCase() ||
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          blockCode.toLowerCase()
      ) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }
    // Show
    sessionStorage.setItem("rateExcluded", "false");
    return true;
  },
  /** Uses waitUntilExists to check if the user should see widget
   * @param handler
   */
  addWidgetListener: (handler) => {
    const timeout = waitForSelectors(
      Cloudbeds.selectors.widget.waitFor,
      handler
    );
    if (Cloudbeds.activeIntervals) {
      Cloudbeds.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on rates page then executes the passed handler function
   * @param handler
   */
  addRatesPageListener: (handler) => {
    const timeout = waitForSelectors(
      Cloudbeds.selectors.ratesPage.waitFor,
      handler
    );
    if (Cloudbeds.activeIntervals) {
      Cloudbeds.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on payment page then executes the passed handler function
   * @param handler
   */
  addPaymentPageListener: (handler) => {
    const timeout = waitForSelectors(
      Cloudbeds.selectors.paymentPage.waitFor,
      handler
    );
    if (Cloudbeds.activeIntervals) {
      Cloudbeds.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on confirmation page then executes the passed handler function
   * @param handler
   */
  addConfirmationPageListener: (handler) => {
    const timeout = waitForSelectors(
      Cloudbeds.selectors.confirmationPage.waitFor,
      handler
    );
    if (Cloudbeds.activeIntervals) {
      Cloudbeds.activeIntervals.push(timeout);
    }
  },
  // listen for cancellation number
  addCancellationPageListener: () => {
    if (Cloudbeds.addCancellationhandler) {
      const timeout = waitForSelectors(
        Cloudbeds.selectors.cancellationPage.target,
        Cloudbeds.addCancellationhandler
      );
      if (Cloudbeds.activeIntervals) {
        Cloudbeds.activeIntervals.push(timeout);
      }
    }
  },
  // not yet implemented in be
  addCancellationhandler: () => {},
  // create widget container
  createWidgetContainer: () => {
    document.querySelector(Cloudbeds.selectors.widget.widget)?.remove();
    if (!document.querySelector(Cloudbeds.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Cloudbeds.selectors.widget.target
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Cloudbeds.selectors.widget.widget.substr(1);
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // create mobile widget container
  createWidgetMobileContainer: () => {
    document.querySelector(Cloudbeds.selectors.widget.widget)?.remove();
    if (!document.querySelector(Cloudbeds.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Cloudbeds.selectors.widget.mobileWidgetTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Cloudbeds.selectors.widget.widget.substr(1);
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // create listener on scrol to anchor widget to sticky menu
  // Best solution for mobile static NavBars
  additionalWidgetFunctions: () => {
    if (window.innerWidth <= 991) {
      document.addEventListener("scroll", (e) => {
        if (
          document.querySelector(
            Cloudbeds.selectors.widget.mobileWidgetFixedTarget
          )
        ) {
          const staticNavBar = document.querySelector(
            Cloudbeds.selectors.widget.mobileStaticNavBar
          );

          const staticNavBarBox = staticNavBar.getBoundingClientRect();
          if (
            staticNavBarBox.top < window.innerHeight &&
            staticNavBarBox.bottom >= 0
          ) {
            document.querySelector("#guestbook-sticky-widget")?.setAttribute(
              "style",
              `
                position: fixed;
                z-index: 1000;
                left: 0px;
                width: 100%;
                top: ${
                  staticNavBar.offsetHeight - Math.abs(staticNavBarBox.top)
                }px;
              `
            );
          } else {
            document.querySelector("#guestbook-sticky-widget")?.setAttribute(
              "style",
              `
                position: fixed;
                z-index: 1000;
                left: 0px;
                width: 100%;
                top: 0px;
                `
            );
          }
          document.querySelector(Cloudbeds.selectors.widget.widget)?.remove();
          const stickyWidget = document.querySelector(
            Cloudbeds.selectors.widget.stickyWidget
          );
          if (!stickyWidget) {
            const messageTarget = document.querySelector(
              Cloudbeds.selectors.widget.mobileWidgetFixedTarget
            );
            if (messageTarget) {
              const injectedElement = document.createElement("div");
              injectedElement.id = Cloudbeds.selectors.widget.stickyWidget.substr(
                1
              );
              injectedElement.setAttribute(
                "style",
                `
                  position: fixed;
                  z-index: 1000;
                  left: 0px;
                  width: 100%;
                  top: 0px
                `
              );
              document
                .querySelector(
                  Cloudbeds.selectors.widget.mobileStaticNavBarContentBellow
                )
                .setAttribute("style", "margin-top: 44px");
              messageTarget.insertAdjacentElement("afterend", injectedElement);
            }
          }
        }
      });
    }
  },
  // create payment message container
  createEnrollmentMessageContainer: () => {
    if (
      !document.querySelector(
        Cloudbeds.selectors.paymentPage.enrollmentMessageContainer
      )
    ) {
      const messageTarget = document.querySelector(
        Cloudbeds.selectors.paymentPage.enrollmentTarget
      )?.parentElement?.parentElement?.parentElement;
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Cloudbeds.selectors.paymentPage.enrollmentMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // create confirmation message container
  createConfirmationMessageContainer: () => {
    if (
      !document.querySelector(
        Cloudbeds.selectors.confirmationPage.confirmationMessageContainer
      )
    ) {
      const messageTarget = document.querySelector(
        Cloudbeds.selectors.confirmationPage.messageTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Cloudbeds.selectors.confirmationPage.confirmationMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // set locale
  setLocale: () => {
    window.gb_data.I18n.locale = Cloudbeds.getLanguage();
  },
  // grab subtotal to use to calculate rewards and display on enrollment/widget
  displaySubtotal: () => {
    let displaySubtotal = "0";
    const subtotalContainer = document.querySelector(
      Cloudbeds.selectors.widget.displaySubtotal
    );
    if (subtotalContainer) {
      displaySubtotal = subtotalContainer.innerText.replace(
        Cloudbeds.getRatesCurrency(),
        ""
      );
    }

    if (!window.gb_dotDecimalSeparated) {
      displaySubtotal = displaySubtotal
        .toString()
        .replace(".", "")
        .replace(",", ".");
    }

    return parsePrice(displaySubtotal);
  },
  // save reservation data on the payment page
  savePaymentPageInfo: () => {
    const saveReservationData = (): void => {
      const resData = Cloudbeds.getReservationData();
      sessionStorage.setItem("gbBookingDetails", JSON.stringify(resData));
    };
    document
      .querySelector(Cloudbeds.selectors.paymentPage.saveInfoTarget)
      .addEventListener("click", () => saveReservationData());
  },
  clearAllListeners: () => {
    if (!Cloudbeds.activeIntervals) return;
    let length = Cloudbeds.activeIntervals.length;
    for (let i = 0; i < length; i++) {
      let int = Cloudbeds.activeIntervals.pop();
      clearInterval(int);
    }
  },
  injectIntegration: () => {
    handleIntegrationInjector(Cloudbeds);
  },
  additionalPaymentsPageFunctions: () => {
    if (
      document.querySelectorAll("#gb-override-styles-payment-page").length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-payment-page" type="text/css">
          #guestbook-enrollment {
            margin-bottom: 20px;
          }
          .MuiFormControlLabel-label {
            font-size: 1em!important;
          }
          *[placeholder*="example@email.com"], *[placeholder*="Enter your"] {
            font-size: 14px!important;
          }
          .sc-hmjpBu.ibGtAb {
            font-size: 1.17em!important;
          }
        </style>`
      );
    }
  },
  additionalRatesPageFunctions: () => {
    if (
      document.querySelectorAll("#gb-override-styles-rates-page").length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-rates-page" type="text/css">
          .gb-rates-message {
            display: flex;
            justify-content: flex-end;
          }
          *[placeholder*="example@email.com"], *[placeholder*="Enter your"] {
            font-size: 14px!important;
          }
          ${
            !Cloudbeds.widgetFixedPosition &&
            ".dfCvaX { margin-top: 5px!important; }"
          }
        </style>`
      );
    }
  },
  additionalConfirmationPageFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles-confirmation-page" type="text/css">
         #guestbook-confirm {
          margin: 5px -15px 15px -15px;
         }
       </style>`
    );
  },
};

export default Cloudbeds;
